import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import {
    GetEventDefinitions,
    getEventDefinitions$,
    getNotificationPreferencesError$,
    getNotificationPreferencesLoading$,
    GetUnsubscribeDetails,
    getUnsubscribeDetails$,
    getUnsubscribeSuccess$,
    ResetNotificationPreferencesState,
    UnsubscribeEmail,
    UnsubscribeEmailSuccess,
} from 'taxilla-library';

@Component({
    selector: 'app-unsubscription',
    templateUrl: './unsubscription.component.html',
    styleUrls: ['./unsubscription.component.scss'],
    standalone: false,
})
export class UnsubscriptionComponent implements OnInit, OnDestroy {
    token: string | null = null;
    email = '';
    isExistingCustomer = false;
    loading$ = this.store.select(getNotificationPreferencesLoading$);
    error$ = this.store.select(getNotificationPreferencesError$);
    success$ = this.store.select(getUnsubscribeSuccess$);
    selectedPreference = '';
    /** Tracks which preference button triggered the current API call. */
    loadingPreference = '';
    /** Tracks which preference was successfully submitted — used to disable sub-options. */
    submittedPreference = '';
    /** Captures the preference at dispatch time to avoid async timing issues. */
    private pendingPreference = '';
    unsubscribeRequest = new Subject<void>();

    /** Ordered hierarchy: each entry disables all options that follow it. */
    private readonly preferenceOrder = ['COMPLETE', 'ORG_NOTIFICATIONS', 'EVENT_TYPE'];
    public eventTypesFilters = [{ name: 'all', displayName: 'All', description: 'All' }];

    public eventTypeDisplayName = '';

    private unSubscribedDetails: any;

    /** Returns true if `option` should be disabled based on the submitted preference. */
    isOptionDisabled(option: string): boolean {
        if (!this.submittedPreference) {
            return false;
        }
        const submittedIndex = this.preferenceOrder.indexOf(this.submittedPreference);
        const optionIndex = this.preferenceOrder.indexOf(option);
        return submittedIndex !== -1 && optionIndex >= submittedIndex;
    }

    constructor(private route: ActivatedRoute, private store: Store, private actions$: Actions, private cdr: ChangeDetectorRef) {}

    onUnsubscribe(preference: string): void {
        if (!this.token || !this.email || !preference) {
            return;
        }

        this.loadingPreference = preference;
        // Capture at dispatch time so the success$ callback always has the right value.
        this.pendingPreference = preference;

        this.store.dispatch(
            UnsubscribeEmail({
                token: this.token,
                email: this.email,
                preference: preference,
            })
        );
    }

    onSubmit(): void {
        if (!this.token || !this.email) {
            return;
        }

        // If not existing customer, auto-select COMPLETE
        if (!this.isExistingCustomer) {
            this.onUnsubscribe('COMPLETE');
        } else {
            // If existing customer, use the selected preference
            if (this.selectedPreference) {
                this.onUnsubscribe(this.selectedPreference);
            }
        }
    }

    ngOnInit(): void {
        this.route.queryParams.pipe(takeUntil(this.unsubscribeRequest)).subscribe(params => {
            this.token = params['token'] || null;
            if (this.token) {
                this.store.dispatch(GetUnsubscribeDetails({ token: this.token }));
                this.store.dispatch(GetEventDefinitions({ forSubscription: false }));
                this.store.pipe(select(getUnsubscribeDetails$)).subscribe(details => {
                    if (details) {
                        this.unSubscribedDetails = details;
                        this.email = details.email;
                    }
                });
            } else {
                this.store.dispatch(ResetNotificationPreferencesState());
            }
        });

        // Reset loadingPreference once the API call completes (success or error).
        this.loading$.pipe(takeUntil(this.unsubscribeRequest)).subscribe(loading => {
            if (!loading) {
                this.loadingPreference = '';
            }
        });

        // Listen directly to the UnsubscribeEmailSuccess action — more reliable than
        // the success$ selector which can be memoized and skip repeated emissions.
        this.actions$.pipe(ofType(UnsubscribeEmailSuccess), takeUntil(this.unsubscribeRequest)).subscribe(() => {
            if (this.pendingPreference) {
                this.submittedPreference = this.pendingPreference;
                this.cdr.markForCheck();
            }
        });

        this.store
            .select(getEventDefinitions$)
            .pipe(takeUntil(this.unsubscribeRequest))
            .subscribe(data => {
                data?.forEach(item => {
                    this.eventTypesFilters.push({
                        name: item.eventType,
                        displayName: item.displayName,
                        description: item.description,
                    });
                });
                setTimeout(() => {
                    const eventFilter = this.eventTypesFilters.find(filter => filter.name === this.unSubscribedDetails?.eventType);
                    if (eventFilter) {
                        this.eventTypeDisplayName = eventFilter.displayName;
                    }
                }, 100);
            });
    }

    ngOnDestroy(): void {
        this.unsubscribeRequest.next();
        this.unsubscribeRequest.complete();
    }
}
